import axios from 'axios'
import type { Template, EmailSendRequest, EmailSendResponse, EmailSendLimits, EmailSendQuota, TemplateFilters, FeedbackCreateRequest, FeedbackCreateResponse } from '../types'

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000/api/v1'

console.log('API Client: Base URL:', API_BASE_URL)

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Add auth token to requests if available
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('admin_token')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    
    // Add user email from localStorage if available
    const email = localStorage.getItem('showcase_email') || localStorage.getItem('last_email')
    if (email) {
      config.headers['x-user-email'] = email
    }
    
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Handle 401 errors (unauthorized) by clearing token and redirecting to login
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('admin_token')
      // Only redirect to login if we're not already there and we were trying to access admin endpoints
      if (!window.location.pathname.includes('/admin/login') && 
          (error.config.url?.includes('/admin') || error.config.url?.includes('/templates/'))) {
        window.location.href = '/admin/login'
      }
    }
    return Promise.reject(error)
  }
)

export const apiClient = {
  // Templates
  getTemplates: async (filters: TemplateFilters = {}): Promise<Template[]> => {
    const params = new URLSearchParams()
    
    if (filters.category) params.append('category', filters.category)
    if (filters.search) params.append('search', filters.search)
    if (filters.resource_type) params.append('resource_type', filters.resource_type)
    if (filters.features) params.append('features', filters.features.join(','))
    if (filters.min_popularity) params.append('min_popularity', filters.min_popularity.toString())
    
    const response = await api.get(`/templates/?${params.toString()}`)
    return response.data
  },

  getRecentTemplates: async (days: number = 7, limit: number = 10): Promise<Template[]> => {
    const response = await api.get(`/templates/recent?days=${days}&limit=${limit}`)
    return response.data
  },

  getTemplate: async (id: number): Promise<Template> => {
    const response = await api.get(`/templates/${id}`)
    return response.data
  },

  getCategories: async (): Promise<string[]> => {
    const response = await api.get('/templates/metadata/categories')
    return response.data
  },

  likeTemplate: async (id: number): Promise<{ success: boolean; like_count: number }> => {
    // Use new logging system
    const response = await api.post(`/likes/?template_id=${id}`)
    return {
      success: response.data.success,
      like_count: response.data.like_count
    }
  },

  unlikeTemplate: async (id: number): Promise<{ success: boolean; like_count: number }> => {
    // For now, we'll keep the old system for unlike since we don't have user tracking
    // In a real app, you'd track user likes and allow proper unliking
    const response = await api.delete(`/templates/${id}/like`)
    return response.data
  },

  // Email
  sendEmail: async (request: EmailSendRequest): Promise<EmailSendResponse> => {
    const response = await api.post('/email/send', request)
    return response.data
  },

  // Feedback
  submitFeedback: async (request: FeedbackCreateRequest): Promise<FeedbackCreateResponse> => {
    const response = await api.post('/feedback/', request)
    return response.data
  },

  sendBulkEmails: async (
    templateIds: number[],
    email: string,
    consent: boolean,
    turnstileToken?: string | null
  ): Promise<any> => {
    const response = await api.post('/email/send-bulk', {
      template_ids: templateIds,
      email,
      consent,
      turnstile_token: turnstileToken ?? undefined
    })
    return response.data
  },

  getEmailSendLimits: async (): Promise<EmailSendLimits> => {
    const response = await api.get('/email/limits')
    return response.data
  },

  getEmailSendQuota: async (email: string): Promise<EmailSendQuota> => {
    const response = await api.get('/email/send-quota', { params: { email } })
    return response.data
  },

  // Admin
  importTemplates: async (templates: any[]): Promise<any> => {
    const response = await api.post('/admin/import/json', { templates })
    return response.data
  },

  importTemplatesFromCSV: async (file: File): Promise<any> => {
    const formData = new FormData()
    formData.append('file', file)
    
    const response = await api.post('/admin/import/csv', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
    return response.data
  },

  getAnalytics: async (): Promise<any> => {
    const response = await api.get('/admin/analytics')
    return response.data
  },

  getAnalyticsContacts: async (skip: number = 0, limit: number = 100): Promise<any> => {
    const response = await api.get(`/admin/analytics/contacts?skip=${skip}&limit=${limit}`)
    return response.data
  },

  getAnalyticsTemplates: async (skip: number = 0, limit: number = 100): Promise<any> => {
    const response = await api.get(`/admin/analytics/templates?skip=${skip}&limit=${limit}`)
    return response.data
  },

  getAnalyticsSends: async (skip: number = 0, limit: number = 100, status: string = 'sent'): Promise<any> => {
    const response = await api.get(`/admin/analytics/sends?skip=${skip}&limit=${limit}&status=${status}`)
    return response.data
  },

  getDailyStats: async (days: number = 30): Promise<any> => {
    const response = await api.get(`/admin/analytics/daily-stats?days=${days}`)
    return response.data
  },

  // Single template operations
  addTemplate: async (template: any): Promise<any> => {
    const response = await api.post('/templates/', template)
    return response.data
  },

  deleteTemplate: async (templateId: number): Promise<any> => {
    const response = await api.delete(`/templates/${templateId}`)
    return response.data
  },

  updateTemplate: async (templateId: number, template: any): Promise<any> => {
    const response = await api.put(`/templates/${templateId}`, template)
    return response.data
  },

  updateTemplateStats: async (templateId: number, stats: { like_count?: number; view_count?: number; send_count?: number }): Promise<any> => {
    const response = await api.patch(`/templates/${templateId}/stats`, stats)
    return response.data
  },

  // Resources
  getAllResources: async (skip: number = 0, limit: number = 1000): Promise<any[]> => {
    const response = await api.get(`/resources/?skip=${skip}&limit=${limit}`)
    return response.data
  },

  getResourcesCount: async (): Promise<{total: number}> => {
    const response = await api.get('/resources/count')
    return response.data
  },

  getRecentResources: async (days: number = 7, limit: number = 20): Promise<any[]> => {
    const response = await api.get(`/resources/recent?days=${days}&limit=${limit}`)
    return response.data
  },

  getResourcesByTemplate: async (esputnikMessageId: string): Promise<any[]> => {
    const response = await api.get(`/resources/template/${esputnikMessageId}`)
    return response.data
  },

  getResourceTypes: async (): Promise<string[]> => {
    const response = await api.get('/resources/types')
    return response.data
  },

  importResourcesFromCSV: async (file: File): Promise<any> => {
    const formData = new FormData()
    formData.append('file', file)
    
    const response = await api.post('/resources/import/csv', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
    return response.data
  },

  deleteResource: async (resourceId: number): Promise<any> => {
    const response = await api.delete(`/resources/${resourceId}`)
    return response.data
  },

  // Likes API
  createLike: async (templateId: number): Promise<any> => {
    const response = await api.post(`/likes/?template_id=${templateId}`)
    return response.data
  },

  getLikeAnalytics: async (): Promise<any> => {
    const response = await api.get('/admin/analytics/likes')
    return response.data
  },

  getViewsAnalytics: async (): Promise<any> => {
    const response = await api.get('/admin/analytics/views')
    return response.data
  },

  createView: async (templateId: number): Promise<any> => {
    console.log('API Client: Creating view for template ID:', templateId)
    try {
      const response = await api.post(`/views/?template_id=${templateId}`)
      console.log('API Client: View created successfully:', response.data)
      return response.data
    } catch (error) {
      console.error('API Client: Failed to create view:', error)
      throw error
    }
  },

  getRecentLikes: async (limit: number = 50, offset: number = 0): Promise<any[]> => {
    const response = await api.get(`/likes/recent?limit=${limit}&offset=${offset}`)
    return response.data
  },

  getRecentViews: async (limit: number = 50, offset: number = 0): Promise<any[]> => {
    const response = await api.get(`/views/recent?limit=${limit}&offset=${offset}`)
    return response.data
  },

  getTemplateLikes: async (templateId: number, limit: number = 20): Promise<any[]> => {
    const response = await api.get(`/likes/template/${templateId}?limit=${limit}`)
    return response.data
  },

  // Demo Videos
  getDemoVideosByTemplate: async (esputnikMessageId: string): Promise<any[]> => {
    const response = await api.get(`/demo-videos/template/${esputnikMessageId}`)
    return response.data
  },

  getDemoVideo: async (videoId: number): Promise<any> => {
    const response = await api.get(`/demo-videos/${videoId}`)
    return response.data
  },

  createDemoVideo: async (video: any): Promise<any> => {
    const response = await api.post('/demo-videos/', video)
    return response.data
  },

  updateDemoVideo: async (videoId: number, video: any): Promise<any> => {
    const response = await api.put(`/demo-videos/${videoId}`, video)
    return response.data
  },

  deleteDemoVideo: async (videoId: number): Promise<any> => {
    const response = await api.delete(`/demo-videos/${videoId}`)
    return response.data
  },

  importDemoVideosFromCSV: async (file: File): Promise<any> => {
    const formData = new FormData()
    formData.append('file', file)
    
    const response = await api.post('/demo-videos/import/csv', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
    return response.data
  }
}

export { api }
